import {
  runRules as coreRunRules,
  type DomainEvent,
  type ModuleId,
  type Mutation,
  type Rule,
  type RuleContext,
} from '@tahi/core';
import { showConnection } from '../../ui/connectionStore';
import type { Store } from '../../ui/store';
import type { LocalDb } from '../localDb';
import { monotonicNow } from '../mutation';
import { loadReadModel } from '../readModel';
import { applyLocal } from '../writes';
import { RULES } from './rules';

export interface CommandEnv {
  db: LocalDb;
  userId: string;
  tz: string;
  usable(moduleId: ModuleId): boolean;
  /** The sync client; `status` lets a hook wait for this session's first sync (tests may omit it). */
  sync: { schedule(delayMs?: number): void; status?: Store<{ lastSyncedAt: string | null }> };
}

export interface CommandInput {
  mutations: Mutation[];
  event?: DomainEvent;
  toast?: { headline: string; details?: Partial<Record<ModuleId, string>> };
}

export interface CommandResult {
  mutations: Mutation[];
  chain: ModuleId[];
}

export interface DispatchDeps {
  rules: Rule[];
  runRules: typeof coreRunRules;
}

/**
 * The single path for changes (conventions §5): primary mutations plus the connected ones core
 * rules add, applied in one applyLocal transaction, then a sync is scheduled.
 */
export async function runCommand(
  env: CommandEnv,
  input: CommandInput,
  deps: DispatchDeps = { rules: RULES, runRules: coreRunRules },
): Promise<CommandResult> {
  let extra: Mutation[] = [];
  let chain: ModuleId[] = [];
  if (input.event) {
    const ctx: RuleContext = {
      userId: env.userId,
      tz: env.tz,
      // Monotonic like every local write: later than the event's own write (rules stamp after it)
      // and earlier than this device's next write, so a later edit always wins over a rule's write.
      now: monotonicNow(),
      usable: env.usable,
      read: await loadReadModel(env.db, env.userId),
    };
    ({ mutations: extra, chain } = deps.runRules(input.event, ctx, deps.rules));
  }
  const all = [...input.mutations, ...extra];
  if (all.length > 0) {
    await applyLocal({ db: env.db, userId: env.userId }, all);
    env.sync.schedule();
  }
  if (input.toast && chain.length > 1)
    showConnection(input.toast.headline, chain, input.toast.details);
  return { mutations: all, chain };
}
